import React, { useState, useEffect } from 'react';
import { HardDrive, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StorageIndicator = ({ className = "", refreshTrigger = 0 }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchStorageData();
    }
  }, [user, refreshTrigger]);

  const fetchStorageData = async () => {
    try {
      // Try with user.id first, if that fails try with user.email
      const identifier = user.id || user.email;
      const timestamp = new Date().getTime(); // Cache buster
      const response = await fetch(`${BACKEND_URL}/api/researchers/storage/${identifier}?_=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStorageData(data);
      } else if (user.email && user.id !== user.email) {
        // If failed with id, try with email
        const emailResponse = await fetch(`${BACKEND_URL}/api/researchers/storage/${user.email}?_=${timestamp}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (emailResponse.ok) {
          const data = await emailResponse.json();
          setStorageData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching storage data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border shadow ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-sm text-gray-500">Loading storage info...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!storageData) {
    return null;
  }

  // Helper function to translate storage units
  const translateStorageUnit = (text) => {
    if (!text) return text;
    return text
      .replace(/\sGB/g, ` ${t('gb')}`)
      .replace(/\sMB/g, ` ${t('mb')}`)
      .replace(/\sKB/g, ` ${t('kb')}`)
      .replace(/\sBytes/g, ` ${t('bytes')}`);
  };

  const percentage = storageData.storage_percentage;
  const isWarning = percentage > 70;
  const isCritical = percentage > 90;

  return (
    <div className={`bg-white rounded-lg border shadow ${className}`}>
      <div className="p-4 pb-3">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          {t('storageUsage')}
          {isCritical && (
            <AlertCircle className="h-4 w-4 text-red-600 ml-auto" />
          )}
        </h3>
      </div>
      <div className="px-4 pb-4">
        <div className="space-y-3">
          {/* Storage Display */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">
              {translateStorageUnit(storageData.total_storage_display)}
            </span>
            <span className="text-sm text-gray-500">
              {t('of')} {translateStorageUnit(storageData.storage_limit_display)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                isCritical ? 'bg-red-600' :
                isWarning ? 'bg-yellow-500' :
                'bg-green-600'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            ></div>
          </div>

          {/* Percentage and Remaining */}
          <div className="flex justify-between items-center text-sm">
            <span className={`font-semibold ${
              isCritical ? 'text-red-600' :
              isWarning ? 'text-yellow-600' :
              'text-green-600'
            }`}>
              {percentage.toFixed(1)}% {t('used')}
            </span>
            <span className="text-gray-600">
              {translateStorageUnit(storageData.storage_remaining_display)} {t('left')}
            </span>
          </div>

          {/* Breakdown */}
          <div className="pt-3 border-t space-y-2 text-xs text-gray-600">
            <div className="flex justify-between">
              <span>{t('libraryDocuments')} ({storageData.library_documents_count})</span>
              <span className="font-medium">{translateStorageUnit(storageData.library_storage_display)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('projectFiles')} ({storageData.project_files_count})</span>
              <span className="font-medium">{translateStorageUnit(storageData.project_storage_display)}</span>
            </div>
          </div>

          {/* Plan Badge */}
          <div className="pt-2">
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              storageData.subscription_plan === 'advanced' ? 'bg-purple-100 text-purple-800' :
              storageData.subscription_plan === 'premium' ? 'bg-green-100 text-green-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {storageData.subscription_plan === 'advanced' ? t('advancedPlan') :
               storageData.subscription_plan === 'premium' ? t('premiumPlan') :
               t('basicPlan')}
            </span>
          </div>

          {/* Warning Message */}
          {isCritical && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              ⚠️ Storage almost full! Consider upgrading your plan or deleting unused files.
            </div>
          )}
          {isWarning && !isCritical && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              💡 You're using more than 70% of your storage. Consider upgrading for more space.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageIndicator;
